import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Icon,
  Input,
  NumberInput,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  Textarea,
  useToast,
} from "@app/ui";
import {
  downloadDisposalExport,
  getDisposalOverview,
  publishDisposalReport,
  saveDisposalReport,
} from "../api/dpp.js";
import { useCan } from "../auth/useCan.js";
import { ErrorAlert } from "../errors/index.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { NoOrgState } from "../orgs/NoOrgState.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { DISPOSAL_CATEGORIES, DISPOSAL_REASONS } from "../types.js";
import type {
  DisposalCategory,
  DisposalLineBody,
  DisposalOverview,
  DisposalReason,
  DisposalReport,
} from "../types.js";

/** Numeric drafts ride as strings; converted on save. */
interface LineDraft {
  productCategory: DisposalCategory;
  categoryNote: string;
  unitCount: string;
  weightKg: string;
  reason: DisposalReason;
  reasonNote: string;
  exemptionRef: string;
  preparedForReusePct: string;
  recycledPct: string;
  otherRecoveryPct: string;
  disposedPct: string;
  measuresNote: string;
}

const EMPTY_LINE: LineDraft = {
  productCategory: "apparel",
  categoryNote: "",
  unitCount: "0",
  weightKg: "0",
  reason: "damaged_unrepairable",
  reasonNote: "",
  exemptionRef: "",
  preparedForReusePct: "0",
  recycledPct: "0",
  otherRecoveryPct: "0",
  disposedPct: "100",
  measuresNote: "",
};

const num = (v: string) => (v.trim() === "" ? 0 : Number(v));

function draftsOf(r: DisposalReport | undefined): LineDraft[] {
  return (r?.lines ?? []).map((l) => ({
    productCategory: l.productCategory,
    categoryNote: l.categoryNote ?? "",
    unitCount: String(l.unitCount),
    weightKg: String(l.weightKg),
    reason: l.reason,
    reasonNote: l.reasonNote ?? "",
    exemptionRef: l.exemptionRef ?? "",
    preparedForReusePct: String(l.preparedForReusePct),
    recycledPct: String(l.recycledPct),
    otherRecoveryPct: String(l.otherRecoveryPct),
    disposedPct: String(l.disposedPct),
    measuresNote: l.measuresNote ?? "",
  }));
}

function bodyOf(d: LineDraft): DisposalLineBody {
  const opt = (v: string) => (v.trim() === "" ? undefined : v.trim());
  return {
    productCategory: d.productCategory,
    ...(opt(d.categoryNote) ? { categoryNote: opt(d.categoryNote)! } : {}),
    unitCount: Math.round(num(d.unitCount)),
    weightKg: num(d.weightKg),
    reason: d.reason,
    ...(opt(d.reasonNote) ? { reasonNote: opt(d.reasonNote)! } : {}),
    ...(opt(d.exemptionRef) ? { exemptionRef: opt(d.exemptionRef)! } : {}),
    preparedForReusePct: num(d.preparedForReusePct),
    recycledPct: num(d.recycledPct),
    otherRecoveryPct: num(d.otherRecoveryPct),
    disposedPct: num(d.disposedPct),
    ...(opt(d.measuresNote) ? { measuresNote: opt(d.measuresNote)! } : {}),
  };
}

const shareSum = (d: LineDraft) =>
  num(d.preparedForReusePct) + num(d.recycledPct) + num(d.otherRecoveryPct) + num(d.disposedPct);

/**
 * Annual unsold-goods destruction disclosure (draft TR ESPR Art. 24): pick a
 * fiscal year, fill the lines, save the draft, publish — the public page at
 * /o/<org>/unsold-disclosure lists published years.
 */
export function DisclosuresPage() {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const can = useCan();
  const { org, loading: orgsLoading } = useOrgs();
  const [overview, setOverview] = useState<DisposalOverview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [year, setYear] = useState<number>(new Date().getUTCFullYear() - 1);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const canWrite = can("disclosure:manage");

  const load = async (orgId: string) => {
    setError(null);
    try {
      const data = await getDisposalOverview(orgId);
      setOverview(data);
      const report = data.reports.find((r) => r.fiscalYear === year);
      setNote(report?.note ?? "");
      setLines(draftsOf(report));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    if (org) void load(org.id);
  }, [org?.id]);
  useEffect(() => {
    const report = overview?.reports.find((r) => r.fiscalYear === year);
    setNote(report?.note ?? "");
    setLines(draftsOf(report));
  }, [year]);

  if (orgsLoading) return <Spin />;
  if (!org) return <NoOrgState />;
  if (error && !overview) return <ErrorAlert error={error} onRetry={() => void load(org.id)} />;
  if (!overview) return <Spin />;

  const report = overview.reports.find((r) => r.fiscalYear === year);
  const locked = report?.status === "published";
  const invalid = lines.some((l) => Math.abs(shareSum(l) - 100) >= 0.01);
  const years = Array.from({ length: 8 }, (_, i) => new Date().getUTCFullYear() - i);
  const update = (idx: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveDisposalReport(org.id, year, {
        ...(note.trim() ? { note: note.trim() } : {}),
        lines: lines.map(bodyOf),
      });
      toast.success(t("disclosure.saved"));
      await load(org.id);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };
  const publish = async () => {
    setSaving(true);
    setError(null);
    try {
      await publishDisposalReport(org.id, year);
      toast.success(t("disclosure.published"));
      await load(org.id);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };
  const exportJson = async () => {
    try {
      const blob = await downloadDisposalExport(org.id, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `unsold-disclosure-${org.slug}-${year}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    }
  };

  const publicUrl = `${window.location.origin}/o/${encodeURIComponent(org.slug)}/unsold-disclosure`;
  const app = overview.applicability;

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Card title={t("disclosure.title")}>
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Text small secondary>
            {t("disclosure.hint")}
          </Text>
          {app.unknown ? (
            <Alert type="warning">{t("disclosure.sizeUnknown")}</Alert>
          ) : app.from ? (
            <Alert type={app.applies ? "info" : "warning"}>
              {t(app.applies ? "disclosure.banApplies" : "disclosure.banFuture").replace("{date}", app.from)}
            </Alert>
          ) : (
            <Alert type="info">{t("disclosure.exempt")}</Alert>
          )}
          <Space wrap>
            <Text small secondary>
              {t("disclosure.publicPage")}:
            </Text>
            <Text code copyable={{ text: publicUrl }}>
              {publicUrl}
            </Text>
          </Space>
          {error != null && <ErrorAlert error={error} />}
          <Table
            rowKey={(r) => String(r.fiscalYear)}
            empty={t("disclosure.noReports")}
            columns={[
              { key: "year", title: t("disclosure.year"), render: (r: DisposalReport) => r.fiscalYear },
              {
                key: "status",
                title: t("common.status"),
                render: (r: DisposalReport) => (
                  <Tag color={r.status === "published" ? "success" : "warning"}>
                    {t(`disclosure.status.${r.status}`)}
                  </Tag>
                ),
              },
              { key: "lines", title: t("disclosure.lines"), render: (r: DisposalReport) => r.lines.length },
              {
                key: "publishedAt",
                title: t("disclosure.publishedAt"),
                render: (r: DisposalReport) => (r.publishedAt ? fmtDate(r.publishedAt) : "—"),
              },
              {
                key: "actions",
                title: "",
                render: (r: DisposalReport) => (
                  <Button size="sm" onClick={() => setYear(r.fiscalYear)}>
                    {t("common.edit")}
                  </Button>
                ),
              },
            ]}
            data={overview.reports}
          />
        </Space>
      </Card>

      <Card
        title={`${t("disclosure.year")} ${year}`}
        extra={
          <Space size="sm">
            <div style={{ width: 120 }}>
              <Select
                options={years.map((y) => ({ label: String(y), value: String(y) }))}
                value={String(year)}
                onChange={(v) => setYear(Number(v))}
              />
            </div>
            {report && (
              <Button
                size="sm"
                variant="subtle"
                icon={<Icon name="download" size={14} />}
                onClick={() => void exportJson()}
              >
                JSON
              </Button>
            )}
            {canWrite && !locked && (
              <Button
                size="sm"
                variant="primary"
                loading={saving}
                disabled={invalid}
                onClick={() => void save()}
              >
                {t("disclosure.saveDraft")}
              </Button>
            )}
            {canWrite && report && !locked && (
              <Popconfirm
                title={t("disclosure.publishConfirm")}
                description={t("disclosure.publishDescription")}
                okText={t("disclosure.publish")}
                cancelText={t("common.cancel")}
                onConfirm={() => void publish()}
              >
                <Button size="sm" loading={saving}>
                  {t("disclosure.publish")}
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {locked && <Alert type="info">{t("disclosure.locked")}</Alert>}
          <Textarea
            label={t("disclosure.note")}
            rows={2}
            disabled={!canWrite || locked}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {lines.map((l, idx) => (
            <Card key={idx}>
              <Space direction="vertical" size="sm" style={{ width: "100%" }}>
                <Space wrap>
                  <div style={{ width: 200 }}>
                    <Select
                      label={t("disclosure.category")}
                      disabled={!canWrite || locked}
                      options={DISPOSAL_CATEGORIES.map((c) => ({
                        label: t(`disposalCategory.${c}`),
                        value: c,
                      }))}
                      value={l.productCategory}
                      onChange={(v) => update(idx, { productCategory: v as DisposalCategory })}
                    />
                  </div>
                  <Input
                    label={t("disclosure.categoryNote")}
                    disabled={!canWrite || locked}
                    value={l.categoryNote}
                    onChange={(e) => update(idx, { categoryNote: e.target.value })}
                  />
                  <NumberInput
                    label={t("disclosure.units")}
                    min={0}
                    disabled={!canWrite || locked}
                    value={l.unitCount}
                    onChange={(v) => update(idx, { unitCount: v })}
                  />
                  <NumberInput
                    label={t("disclosure.weightKg")}
                    min={0}
                    disabled={!canWrite || locked}
                    value={l.weightKg}
                    onChange={(v) => update(idx, { weightKg: v })}
                  />
                </Space>
                <Space wrap>
                  <div style={{ width: 260 }}>
                    <Select
                      label={t("disclosure.reason")}
                      disabled={!canWrite || locked}
                      options={DISPOSAL_REASONS.map((r) => ({ label: t(`disposalReason.${r}`), value: r }))}
                      value={l.reason}
                      onChange={(v) => update(idx, { reason: v as DisposalReason })}
                    />
                  </div>
                  <Input
                    label={t("disclosure.reasonNote")}
                    disabled={!canWrite || locked}
                    value={l.reasonNote}
                    onChange={(e) => update(idx, { reasonNote: e.target.value })}
                  />
                  <Input
                    label={t("disclosure.exemptionRef")}
                    disabled={!canWrite || locked}
                    value={l.exemptionRef}
                    onChange={(e) => update(idx, { exemptionRef: e.target.value })}
                  />
                </Space>
                <Space wrap>
                  <NumberInput
                    label={t("disclosure.reusePct")}
                    min={0}
                    max={100}
                    suffix="%"
                    disabled={!canWrite || locked}
                    value={l.preparedForReusePct}
                    onChange={(v) => update(idx, { preparedForReusePct: v })}
                  />
                  <NumberInput
                    label={t("disclosure.recycledPct")}
                    min={0}
                    max={100}
                    suffix="%"
                    disabled={!canWrite || locked}
                    value={l.recycledPct}
                    onChange={(v) => update(idx, { recycledPct: v })}
                  />
                  <NumberInput
                    label={t("disclosure.otherRecoveryPct")}
                    min={0}
                    max={100}
                    suffix="%"
                    disabled={!canWrite || locked}
                    value={l.otherRecoveryPct}
                    onChange={(v) => update(idx, { otherRecoveryPct: v })}
                  />
                  <NumberInput
                    label={t("disclosure.disposedPct")}
                    min={0}
                    max={100}
                    suffix="%"
                    disabled={!canWrite || locked}
                    value={l.disposedPct}
                    onChange={(v) => update(idx, { disposedPct: v })}
                  />
                  {Math.abs(shareSum(l) - 100) >= 0.01 && (
                    <Tag color="error">{t("disclosure.sharesInvalid")}</Tag>
                  )}
                </Space>
                <Textarea
                  label={t("disclosure.measures")}
                  rows={2}
                  disabled={!canWrite || locked}
                  value={l.measuresNote}
                  onChange={(e) => update(idx, { measuresNote: e.target.value })}
                />
                {canWrite && !locked && (
                  <div>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      {t("common.remove")}
                    </Button>
                  </div>
                )}
              </Space>
            </Card>
          ))}
          {canWrite && !locked && (
            <div>
              <Button
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
              >
                {t("disclosure.addLine")}
              </Button>
            </div>
          )}
        </Space>
      </Card>
    </Space>
  );
}
